import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import ReactDatePicker from 'react-datepicker';
import Select, { components, ValueType } from 'react-select';

import Button, {
  ButtonSize,
  ButtonType
} from '../shared/components/core-ui/button/button.component';
import { birdToast } from '../shared/components/core-ui/toast';
import { ErrorInfo, isErrorInfo } from '../shared/interfaces/error-info.interface';
import { getAllUnitTypes, getUnitTypeRooms } from '../shared/services/data/unit-type.data';
import { getAllAreas, getAllUnitTypeGroups } from '../shared/services/data/lookups.data';
import {
  RoomsUiModelResponse,
  UnitTypeUiModelResponse
} from '../shared/ui-models/unit-type.ui-model';
import { AreaUiModel } from '../shared/ui-models/area.ui-model';
import { UnitTypeGroupUiModel } from '../shared/ui-models/unit-type-group.ui-model';
import { LoadingData } from '../ui/LoadingData';
import { NotFoundData } from '../ui/NotFoundData';
import { Pagination } from '../shared/components/core-ui/pagination/pagination.component';
import { getOccupancyRates } from '../KpisModule/shared/services/data.service';
import { getAllProperties } from '../PropertyModule/shared/services/data.service';
import { PropertyUiModel } from '../shared/ui-models/property.ui-model';

const MODULE_NAME = 'OCCUPANCY_RATE_MODULE';

const getModuleName = () => MODULE_NAME;

type OccupancyRateItem = {
  unitId: string;
  name: string;
  overAllOccupancy: string;
  occupancyWithoutBlockDates: string;
  blockedDays: number;
  availableDays: number;
  totalDays: number;
};

type OccupancyRateQuery = {
  searchDateFrom: number;
  searchDateTo: number;
  unitTypeId?: number;
  unitTypeGroupId?: string;
  propertyIds?: string[];
  areaIds?: string[];
  roomIds?: string[];
};

const getPercentValue = (value: string) =>
  Number(
    String(value || '0')
      .replace('%', '')
      .trim()
  ) || 0;

const getOccupancyTone = (value: string) => {
  const percent = getPercentValue(value);
  if (percent >= 85) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (percent >= 60) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-rose-50 text-rose-700 border-rose-200';
};

const MultiValue = (props: any) => {
  const maxToShow = props.selectProps.maxToShow;
  const length = props.getValue().length;
  const shouldBadgeShow = length > maxToShow;
  const displayLength = length - maxToShow;

  if (props.index < maxToShow) {
    return <components.MultiValue {...props} />;
  }

  if (props.index === maxToShow) {
    return shouldBadgeShow ? (
      <div className="text-xs text-neutral-500 px-2 py-1 bg-gray-100 rounded-md">
        + {displayLength} selected
      </div>
    ) : null;
  }

  return null;
};

export default function OccupancyRateContainer() {
  const [loadingData, setLoadingData] = useState(false);
  const [items, setItems] = useState<OccupancyRateItem[]>([]);
  const [offset, setOffset] = useState(1);
  const [limit, setLimit] = useState(20);

  const initialMonthFrom = moment().startOf('month').toDate();
  const initialMonthTo = moment().endOf('month').toDate();

  const [unitTypeId, setUnitTypeId] = useState<number | undefined>(undefined);
  const [searchDateFrom, setSearchDateFrom] = useState<Date>(initialMonthFrom);
  const [searchDateTo, setSearchDateTo] = useState<Date>(initialMonthTo);
  const [unitTypeGroupId, setUnitTypeGroupId] = useState('');
  const [propertyIds, setPropertyIds] = useState<string[]>([]);
  const [areaIds, setAreaIds] = useState<string[]>([]);
  const [roomIds, setRoomIds] = useState<string[]>([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const [appliedQuery, setAppliedQuery] = useState<OccupancyRateQuery>({
    searchDateFrom: initialMonthFrom.getTime(),
    searchDateTo: initialMonthTo.getTime()
  });
  const [unitTypeOptions, setUnitTypeOptions] = useState<{ value: number; label: string }[]>([]);
  const [unitTypeGroupsList, setUnitTypeGroupsList] = useState<{ value: string; label: string }[]>(
    []
  );
  const [areaOptions, setAreaOptions] = useState<{ value: string; label: string }[]>([]);
  const [propertyOptions, setPropertyOptions] = useState<{ value: string; label: string }[]>([]);
  const [roomsOptions, setRoomsOptions] = useState<{ value: string; label: string }[]>([]);

  const load = React.useCallback(
    (query: OccupancyRateQuery = appliedQuery) => {
      setLoadingData(true);
      getOccupancyRates(query).then((res) => {
        setLoadingData(false);
        if (isErrorInfo(res)) {
          setItems([]);
          return;
        }

        setItems(res);
      });
    },
    [appliedQuery]
  );

  const buildDraftQuery = (): OccupancyRateQuery => ({
    searchDateFrom: searchDateFrom.getTime(),
    searchDateTo: searchDateTo.getTime(),
    ...(unitTypeId ? { unitTypeId } : {}),
    ...(unitTypeGroupId ? { unitTypeGroupId } : {}),
    ...(propertyIds.length ? { propertyIds } : {}),
    ...(areaIds.length ? { areaIds } : {}),
    ...(roomIds.length ? { roomIds } : {})
  });

  const pagedItems = useMemo(() => {
    const start = (offset - 1) * limit;
    return items.slice(start, start + limit);
  }, [items, limit, offset]);

  const filterTags = useMemo(() => {
    const tags: { label: string; value: string }[] = [];

    if (appliedQuery.unitTypeId) {
      const unitType = unitTypeOptions.find((u) => u.value === appliedQuery.unitTypeId);
      if (unitType) tags.push({ label: 'Unit Type', value: unitType.label });
    }

    if (appliedQuery.unitTypeGroupId) {
      const unitTypeGroup = unitTypeGroupsList.find(
        (u) => u.value === appliedQuery.unitTypeGroupId
      );
      if (unitTypeGroup) tags.push({ label: 'Unit Type Group', value: unitTypeGroup.label });
    }

    if (appliedQuery.propertyIds?.length) {
      const selectedProperties = appliedQuery.propertyIds
        .map(
          (propertyId) => propertyOptions.find((property) => property.value === propertyId)?.label
        )
        .filter(Boolean)
        .join(', ');

      if (selectedProperties) tags.push({ label: 'Property', value: selectedProperties });
    }

    if (appliedQuery.areaIds?.length) {
      const selectedAreas = appliedQuery.areaIds
        .map((areaId) => areaOptions.find((area) => area.value === areaId)?.label)
        .filter(Boolean)
        .join(', ');

      if (selectedAreas) tags.push({ label: 'Area', value: selectedAreas });
    }

    if (appliedQuery.roomIds?.length) {
      const selectedRooms = appliedQuery.roomIds
        .map((roomId) => roomsOptions.find((room) => room.value === roomId)?.label)
        .filter(Boolean)
        .join(', ');

      if (selectedRooms) tags.push({ label: 'Room Name', value: selectedRooms });
    }

    tags.push({ label: 'From', value: moment(appliedQuery.searchDateFrom).format('MM/DD/YYYY') });
    tags.push({ label: 'To', value: moment(appliedQuery.searchDateTo).format('MM/DD/YYYY') });

    return tags;
  }, [
    appliedQuery,
    unitTypeOptions,
    unitTypeGroupsList,
    propertyOptions,
    areaOptions,
    roomsOptions
  ]);

  const applyFilters = () => {
    const newQuery = buildDraftQuery();
    setAppliedQuery(newQuery);
    setOffset(1);
    load(newQuery);
  };

  const clearFilters = () => {
    const defaultQuery: OccupancyRateQuery = {
      searchDateFrom: initialMonthFrom.getTime(),
      searchDateTo: initialMonthTo.getTime()
    };

    setUnitTypeId(undefined);
    setUnitTypeGroupId('');
    setPropertyIds([]);
    setAreaIds([]);
    setRoomIds([]);
    setSearchDateFrom(initialMonthFrom);
    setSearchDateTo(initialMonthTo);
    setAppliedQuery(defaultQuery);
    setOffset(1);
    load(defaultQuery);
  };

  const clearSingleFilter = (filterLabel: string) => {
    const newQuery = { ...appliedQuery };

    switch (filterLabel) {
      case 'Unit Type':
        setUnitTypeId(undefined);
        delete newQuery.unitTypeId;
        break;
      case 'Unit Type Group':
        setUnitTypeGroupId('');
        delete newQuery.unitTypeGroupId;
        break;
      case 'Property':
        setPropertyIds([]);
        delete newQuery.propertyIds;
        break;
      case 'Area':
        setAreaIds([]);
        delete newQuery.areaIds;
        break;
      case 'Room Name':
        setRoomIds([]);
        delete newQuery.roomIds;
        break;
      case 'From':
      case 'To':
        return;
    }

    setAppliedQuery(newQuery);
    setOffset(1);
    load(newQuery);
  };

  useEffect(() => {
    load({
      searchDateFrom: initialMonthFrom.getTime(),
      searchDateTo: initialMonthTo.getTime()
    });

    getAllUnitTypes('reservation-module', 0, -1, {}).then(
      (response: UnitTypeUiModelResponse | any) => {
        if (!isErrorInfo(response)) {
          setUnitTypeOptions(
            response?.unitList?.map((u: any) => ({ value: Number(u.id), label: u.name || '' })) ||
              []
          );
        }
      }
    );

    getAllUnitTypeGroups(MODULE_NAME).then((response: UnitTypeGroupUiModel[] | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setUnitTypeGroupsList(
          response?.map((group) => ({ value: group.id || '', label: group.name }))
        );
      }
    });

    getAllAreas(MODULE_NAME).then((response: AreaUiModel[] | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setAreaOptions(response?.map((area) => ({ value: area.id || '', label: area.name })) || []);
      }
    });

    getAllProperties().then((response: PropertyUiModel[] | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setPropertyOptions(
          response?.map((property) => ({ value: property.id || '', label: property.name || '' }))
        );
      }
    });

    getUnitTypeRooms(getModuleName()).then((response: RoomsUiModelResponse[] | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setRoomsOptions(response);
      } else {
        birdToast.error('error while getting rooms', 'failed loading unit type rooms');
      }
    });
  }, []);

  function downloadCSV(file: BlobPart) {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(new Blob([file], { type: 'application/csv' }));
    link.setAttribute('download', 'OccupancyRate_data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const exportOccupancyRate = (query: OccupancyRateQuery) => {
    const promise = getOccupancyRates(query, true).then((response) => {
      if (!isErrorInfo(response)) downloadCSV(response);
    });

    birdToast.promise(promise, {
      errorFailedMessage: 'Failed to export occupancy rate',
      loadingMessage: 'Exporting occupancy rate',
      successMessage: 'Occupancy rate exported successfully',
      errorPermissionMessage: 'You do not have permission to export occupancy rate'
    });
  };

  return (
    <div className="min-h-screen bg-creams-50 px-6 py-4">
      <div className="flex flex-row items-center justify-between gap-3 mb-4">
        <span className="text-3xl md:text-4xl font-recoleta font-semibold text-neutral-500">
          Occupancy Rate
        </span>
        <div className="flex flex-row item-center gap-2">
          <Button
            onClick={() => setFiltersExpanded((prev) => !prev)}
            type={ButtonType.primaryInverted}
            size={ButtonSize.small}
          >
            {filtersExpanded ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button
            onClick={() => exportOccupancyRate(appliedQuery)}
            size={ButtonSize.small}
            type={ButtonType.primary}
          >
            Export
          </Button>
        </div>
      </div>

      <div className="overflow-hidden mb-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 p-3 md:p-4">
            <div className="flex flex-wrap items-center gap-2">
              {filterTags.map((tag) => (
                <button
                  type="button"
                  key={`${tag.label}-${tag.value}`}
                  onClick={() => clearSingleFilter(tag.label)}
                  className="inline-flex max-w-full items-center gap-1 text-sm md:text-base text-primary-700 font-gilroy-medium bg-white/90 border border-primary-200 px-2 py-0.5 rounded-full hover:bg-primary-50"
                >
                  <span className="text-neutral-500">{tag.label}:</span>
                  <span className="truncate max-w-[140px] sm:max-w-[180px]">{tag.value}</span>
                  <span>×</span>
                </button>
              ))}
            </div>
          </div>

          {filtersExpanded && (
            <div className="p-3 md:p-4 pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 items-start">
                <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
                  <div className="text-sm font-gilroy-medium text-neutral-500 mb-1">Unit Type</div>
                  <Select
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                    value={{
                      value: unitTypeId ?? 0,
                      label: unitTypeOptions.find((u) => u.value === unitTypeId)?.label || 'All'
                    }}
                    options={[{ value: 0, label: 'All' }, ...unitTypeOptions]}
                    classNamePrefix="react-select"
                    onChange={(value: any) =>
                      setUnitTypeId(value?.value ? Number(value.value) : undefined)
                    }
                  />
                </div>

                <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
                  <div className="text-sm font-gilroy-medium text-neutral-500 mb-1">
                    Unit Type Group
                  </div>
                  <Select
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                    value={{
                      value: unitTypeGroupId,
                      label:
                        unitTypeGroupsList.find((c) => c.value === unitTypeGroupId)?.label || 'All'
                    }}
                    options={[{ value: '', label: 'All' }, ...unitTypeGroupsList]}
                    classNamePrefix="react-select"
                    onChange={(value: ValueType<{ value: string; label: string }, false>) =>
                      setUnitTypeGroupId(value ? value.value : '')
                    }
                  />
                </div>

                <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
                  <div className="text-sm font-gilroy-medium text-neutral-500 mb-1">Area</div>
                  <Select
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                    value={areaOptions.filter((option) => areaIds.includes(option.value))}
                    options={areaOptions}
                    classNamePrefix="react-select"
                    isMulti
                    maxToShow={2}
                    components={{ MultiValue }}
                    closeMenuOnSelect={false}
                    onChange={(value: ValueType<{ value: string; label: string }, true>) =>
                      setAreaIds(value?.length ? value.map((option) => option.value) : [])
                    }
                  />
                </div>

                <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
                  <div className="text-sm font-gilroy-medium text-neutral-500 mb-1">Property</div>
                  <Select
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                    value={propertyOptions.filter((option) => propertyIds.includes(option.value))}
                    options={propertyOptions}
                    classNamePrefix="react-select"
                    isMulti
                    maxToShow={2}
                    components={{ MultiValue }}
                    closeMenuOnSelect={false}
                    onChange={(value: ValueType<{ value: string; label: string }, true>) =>
                      setPropertyIds(value?.length ? value.map((option) => option.value) : [])
                    }
                  />
                </div>

                <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
                  <div className="text-sm font-gilroy-medium text-neutral-500 mb-1">
                    From Date *
                  </div>
                  <ReactDatePicker
                    selected={searchDateFrom}
                    onChange={(date: Date | null) => {
                      if (!date) return;
                      setSearchDateFrom(date);
                      if (searchDateTo.getTime() < date.getTime()) {
                        setSearchDateTo(date);
                      }
                    }}
                    placeholderText="From"
                    className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
                  />
                </div>

                <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60">
                  <div className="text-sm font-gilroy-medium text-neutral-500 mb-1">To Date *</div>
                  <ReactDatePicker
                    selected={searchDateTo}
                    onChange={(date: Date | null) => {
                      if (!date) return;
                      setSearchDateTo(date);
                    }}
                    minDate={searchDateFrom}
                    placeholderText="To"
                    className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
                  />
                </div>

                <div className="rounded-xl border border-gray-100 p-3 bg-gray-50/60 sm:col-span-2 xl:col-span-3 2xl:col-span-2">
                  <div className="text-sm font-gilroy-medium text-neutral-500 mb-1">Room Name</div>
                  <Select
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                    value={roomsOptions.filter((option) => roomIds.includes(option.value))}
                    options={roomsOptions}
                    classNamePrefix="react-select"
                    isMulti
                    maxToShow={2}
                    components={{ MultiValue }}
                    closeMenuOnSelect={false}
                    onChange={(value: ValueType<{ value: string; label: string }, true>) =>
                      setRoomIds(value?.length ? value.map((option) => option.value) : [])
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end mt-4 gap-4">
                <Button
                  onClick={clearFilters}
                  type={ButtonType.primaryInverted}
                  size={ButtonSize.small}
                >
                  Clear
                </Button>
                <Button onClick={applyFilters} size={ButtonSize.small}>
                  Apply Filters
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {loadingData ? (
        <LoadingData />
      ) : (
        <>
          <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden mb-16">
            <div className="w-full overflow-x-auto">
              <table
                className="min-w-[1200px] w-full border-separate border-spacing-0"
                aria-label="Occupancy rate table"
              >
                <thead className="bg-gray-50">
                  <tr className="[&>th]:text-xl [&>th]:font-bold [&>th]:uppercase [&>th]:tracking-wider [&>th]:font-gilroy-bold [&>th]:py-3 text-neutral-500 border-b border-gray-200">
                    <th className="px-4 text-left whitespace-nowrap" scope="col">
                      Name
                    </th>
                    <th className="px-4   text-left whitespace-nowrap" scope="col">
                      Unit ID
                    </th>
                    <th className="px-4 text-left whitespace-nowrap" scope="col">
                      Overall Occupancy
                    </th>
                    <th className="px-4 text-left whitespace-nowrap" scope="col">
                      Occupancy Without Block Dates
                    </th>
                    <th className="px-4 text-left whitespace-nowrap" scope="col">
                      Blocked Days
                    </th>
                    <th className="px-4 text-left whitespace-nowrap" scope="col">
                      Available Days
                    </th>
                    <th className="px-4 text-left whitespace-nowrap" scope="col">
                      Total Days
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.map((item, index) => (
                    <tr
                      key={item.unitId}
                      className={`transition-colors hover:bg-primary-50/40 [&>td]:border-b [&>td]:border-gray-200 [&>td:first-child]:border-l [&>td:last-child]:border-r ${
                        index % 2 ? 'bg-gray-50/40' : 'bg-white'
                      }`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap align-middle">
                        <span className="font-medium font-gilroy-medium text-neutral-500">
                          {item.name}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap align-middle">
                        <span className="font-medium font-gilroy-medium text-neutral-500">
                          {item.unitId}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap align-middle">
                        <span
                          className={`inline-flex min-w-[68px] justify-center items-center rounded-full border px-2.5 py-1 text-lg font-gilroy-medium font-medium ${getOccupancyTone(
                            item.overAllOccupancy
                          )}`}
                        >
                          {item.overAllOccupancy}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap align-middle">
                        <span
                          className={`inline-flex min-w-[68px] justify-center items-center rounded-full border px-2.5 py-1 text-lg font-gilroy-medium font-medium ${getOccupancyTone(
                            item.occupancyWithoutBlockDates
                          )}`}
                        >
                          {item.occupancyWithoutBlockDates}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap align-middle text-center">
                        <span className="font-medium font-gilroy-medium text-neutral-500">
                          {item.blockedDays}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap align-middle text-center">
                        <span className="font-medium font-gilroy-medium text-neutral-500">
                          {item.availableDays}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap align-middle text-center">
                        <span className="font-medium font-gilroy-medium text-neutral-500">
                          {item.totalDays}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {items.length === 0 && <NotFoundData />}

          {items.length > 0 && (
            <div className="fixed bottom-2 inset-x-0 mx-auto w-[90%] lg:w-[60%] flex justify-center items-center">
              <Pagination
                itemCounts={items.length}
                itemsPerPage={limit}
                offset={offset}
                handlePageChange={(newPage) => setOffset(newPage)}
                handleItemsPerPage={(itemsPerPage) => {
                  setLimit(itemsPerPage);
                  setOffset(1);
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
